import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate, Link as RouterLink, useParams, useLocation } from "react-router-dom";
import { MenuItem, FormControl, Box, Typography, Link, TextField, Button } from "@mui/material";
import ArrowCircleLeftOutlinedIcon from '@mui/icons-material/ArrowCircleLeftOutlined';
import PopOverCTA from "../../shared_elements/components/PopOverCTA";
import CustomTabs from "../../shared_elements/components/CustomTabs";
import { trackActivity } from "../../utils/mixpanel";

const LeasesMenuList = ({ type = '', pageLabel = '', btnText = '' }) => {
  const { leaseSlug } = useParams();
  const { permission } = useSelector((state) => state.leases);
  const navigate = useNavigate();
  const location = useLocation();
  const [search, setSearch] = useState('');
  const [tabIndex, setTabIndex] = useState(0);

  const menuItems = [
    { path: `/lease/${leaseSlug}/lease-details/headlines`, label: "Headlines", detailType: "basic", permissionKey: "lease_details" },
    { path: `/lease/${leaseSlug}/lease-details/parties`, label: "Parties", detailType: "basic", permissionKey: "party" },
    { path: `/lease/${leaseSlug}/lease-details/working-groups`, label: "Working Groups", detailType: "basic", permissionKey: "working_groups" },
    { path: `/lease/${leaseSlug}/lease-details/sublease`, label: "Sublease", detailType: "basic", permissionKey: "sublease" },
    { path: `/lease/${leaseSlug}/lease-details/wet-lease`, label: "Wet Lease", detailType: "basic", permissionKey: "wet_lease" },
    { path: `/lease/${leaseSlug}/lease-details/supplement`, label: "Supplement", detailType: "basic", permissionKey: "supplement" },
    { path: `/lease/${leaseSlug}/lease-details/amendment`, label: "Amendment", detailType: "basic", permissionKey: "amendment" },
    { path: `/lease/${leaseSlug}/lease-details/notes`, label: "Notes", detailType: "basic", permissionKey: "qualified_maintenance_event" },
    { path: `/lease/${leaseSlug}/lease-details/security-deposit`, label: "Security Deposit", detailType: "technical", permissionKey: "security_deposit" },
    { path: `/lease/${leaseSlug}/lease-details/insurance`, label: "Insurance & Claim", detailType: "technical", permissionKey: "insurance" },
    { path: `/lease/${leaseSlug}/lease-details/obligations`, label: "Obligations", detailType: "technical", permissionKey: "obligations" },
    { path: `/lease/${leaseSlug}/lease-details/operating-region`, label: "Operating Region Esc.", detailType: "technical", permissionKey: "region_escalation" },
    { path: `/lease/${leaseSlug}/lease-details/late_payment_interest`, label: "Late Payment Interest", detailType: "technical", permissionKey: "late_payment_interest" },
    { path: `/lease/${leaseSlug}/lease-details/contributions`, label: "Contributions", detailType: "technical", permissionKey: "contribution" },
    { path: `/lease/${leaseSlug}/lease-details/related-loans`, label: "Related Loans", detailType: "technical", permissionKey: "related_loan" },
    { path: `/lease/${leaseSlug}/lease-details/delivery-condition`, label: "Delivery Condition", detailType: "terms", permissionKey: "delivery_conditions" },
    { path: `/lease/${leaseSlug}/lease-details/return-condition`, label: "Return Condition", detailType: "terms", permissionKey: "return_conditions" },
    { path: `/lease/${leaseSlug}/lease-details/qualified-maintenance-events`, label: "Qualified Maintenance Events", detailType: "terms", permissionKey: "qualified_maintenance_event" },
    { path: `/lease/${leaseSlug}/lease-details/eol-option`, label: "EOL Option", detailType: "terms", permissionKey: "eol_options" },
    { path: `/lease/${leaseSlug}/lease-details/eol-obligation`, label: "EOL Obligation", detailType: "terms", permissionKey: "eol_obligation" },
    { path: `/lease/${leaseSlug}/lease-details/exception_to_acceptance`, label: "Exception To Acceptance", detailType: "terms", permissionKey: "exceptions_to_acceptance" },
    { path: `/lease/${leaseSlug}/lease-details/induction-condition`, label: "Induction Condition", detailType: "terms", permissionKey: "induction_conditions" },
  ];

  const hasPermission = (item, permissionType = "R") => {
    const modulePermissions =
      permission?.permissions?.lease_revamp_application?.lease_revamp?.[item?.permissionKey];
    return modulePermissions?.includes(permissionType);
  };

  const basicMenus = menuItems.filter(
    (item) => item.detailType === type && hasPermission(item)
  );

  const tabLabels = basicMenus.map((menu) => menu.label);
  const selectedMenuLabel = menuItems.find((menu) => menu.path === location.pathname)?.label || btnText;

  const handleItemClicked = (page_type) => {
    trackActivity('Item Clicked', { page_title: 'Lease Details', page_type: page_type });
  };

  const handleTabChange = (event, newIndex) => {
    setTabIndex(newIndex);

    const selectedMenu = basicMenus[newIndex];
    if (selectedMenu) {
      navigate(selectedMenu.path);
      handleItemClicked(`Lease Details Tab: ${selectedMenu.label}`);
    }
  };

  useEffect(() => {
    const index = basicMenus.findIndex(
      (menu) => menu.path === location.pathname
    );

    if (index !== -1 && index !== tabIndex) {
      setTabIndex(index);
    }
  }, [location.pathname, basicMenus, tabIndex]);

  return (
    <div style={{ marginTop: '5px' }}>
      <FormControl className="lease-detials-tabs" fullWidth sx={{ display: { xs: "none", md: "block" } }}>
        <CustomTabs
          value={tabIndex}
          onChange={handleTabChange}
          tabsList={tabLabels}
          tabVariant="fullWidth"
          alignCenter={true}
        />
      </FormControl>
      <FormControl className="lease-menu-list-container" sx={{ display: { xs: "block", md: "none" } }}>
        <Box className="lease-list-title" sx={{ marginBottom: '5px' }}>
          <Link component={RouterLink} className="lease-list-title-link"
          // to={assetNavLink}
          >
            <ArrowCircleLeftOutlinedIcon />
            {/* <Typography sx={{ fontSize: "13px", fontWeight: "bold", paddingLeft: "5px", textDecoration: "underline", }}> Lease List </Typography> */}
          </Link>
        </Box>
        {pageLabel ?
          <Button size='small' variant='outlined' color='primary' sx={{ cursor: "default", pointerEvents: "none", }}>
            {pageLabel}
          </Button>
          :
          <PopOverCTA
            btnText={selectedMenuLabel || "Menu"}
            onToggle={() => setSearch('')}
            onCloseItem={true}>
            <div className="menu-list-sidebar-container">
              <div style={{ padding: '10px' }}>
                <TextField
                  id="search"
                  variant="outlined"
                  size="small"
                  margin="none"
                  autoFocus
                  slotProps={{ inputLabel: { shrink: true } }}
                  placeholder="Search for Menu list"
                  fullWidth
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="leasses-search-menu-bar"
                />
              </div>
              <div className="menu-list-sidebar">
                {(() => {
                  let basicMenusList = menuItems.filter(item => item.detailType === 'basic').filter(item => item.label.toLowerCase().includes(search.toLowerCase())).filter(item => hasPermission(item));
                  if (basicMenusList?.length) {
                    return (
                      <>
                        <MenuItem className="menu-list-heading">Basic Details</MenuItem>
                        {basicMenusList.map((item) => (
                          <MenuItem className="menu-list-item" onClick={() => { navigate(item.path); handleItemClicked(`Basic Details Menu Item: ${item.label}`); }} key={item.path} value={item.path} selected={location.pathname === item.path}> {item.label} </MenuItem>
                        ))}
                      </>
                    )
                  } else {
                    return null
                  }
                })()}

                {(() => {
                  let technicalMenusList = menuItems.filter(item => item.detailType === 'technical');
                  technicalMenusList = technicalMenusList.filter(item => item.label.toLowerCase().includes(search.toLowerCase())).filter(item => hasPermission(item));
                  if (technicalMenusList?.length) {
                    return (
                      <>
                        <MenuItem className="menu-list-heading">Technical Details</MenuItem>
                        {technicalMenusList.map((item) => (
                          <MenuItem className="menu-list-item" onClick={() => { navigate(item.path); handleItemClicked(`Technical Details Menu Item: ${item.label}`); }} key={item.path} value={item.path} selected={location.pathname === item.path}> {item.label} </MenuItem>
                        ))}
                      </>
                    )
                  } else {
                    return null
                  }
                })()}

                {(() => {
                  let termsMenusList = menuItems.filter(item => item.detailType === 'terms');
                  termsMenusList = termsMenusList.filter(item => item.label.toLowerCase().includes(search.toLowerCase())).filter(item => hasPermission(item));
                  if (termsMenusList?.length) {
                    return (
                      <>
                        <MenuItem className="menu-list-heading">Terms & Conditions</MenuItem>
                        {termsMenusList.map((item) => (
                          <MenuItem className="menu-list-item" onClick={() => { navigate(item.path); handleItemClicked(`Terms & Conditions Menu Item: ${item.label}`); }} key={item.path} value={item.path} selected={location.pathname === item.path}> {item.label} </MenuItem>
                        ))}
                      </>
                    )
                  } else {
                    return null
                  }
                })()}
              </div>
            </div>
          </PopOverCTA>
        }
      </FormControl>
    </div>
  );
};

export default LeasesMenuList;
